import { graphqlPost } from "api/graphqlClient";
import { Button } from "components/Button/Button";
import { SchemaForm } from "components/Form/components/SchemaForm/SchemaForm";
import { SwitchContentFormComponent } from "components/Form/components/SwitchContent/SwitchContentFormComponent";
import { IconCollection } from "components/Icon/IconCollection";
import NotificationService from "components/Notification/Notification.service";
import RestErrorNotification from "components/Notification/RestErrorNotification";
import { SpinnerAnimation } from "components/SpinnerAnimation/SpinnerAnimation";
import { useWorkspaceConfigValue } from "containers/WorkspaceConfig/useWorkspaceConfigValue";
import { Execution } from "graphql/generated/graphql";
import { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";

interface ExecutionInputOutputValueTabProps {
    activeTab: "input" | "output";
    activeTabContent;
    resourceData: Execution;
    recordId: string;
    isExecution: boolean;
    iri: string;
    isExecutionRunning: boolean;
}

export const ExecutionInputOutputValueTab = ({
    activeTab,
    activeTabContent,
    resourceData,
    recordId,
    isExecution,
    iri,
    isExecutionRunning,
}: ExecutionInputOutputValueTabProps) => {
    const dispatch = useDispatch();

    const [isLoading, setLoading] = useState(false);
    const [executionData, setExecutionData] = useState({});

    const alwaysLoadExecutionInputValueLimit = useWorkspaceConfigValue(
        "INPUT_VALUE_SIZE_BYTES_ALWAYS_LOAD_LIMIT"
    );
    const alwaysLoadExecutionOutputValueLimit = useWorkspaceConfigValue(
        "OUTPUT_VALUE_SIZE_BYTES_ALWAYS_LOAD_LIMIT"
    );
    const optionalLoadExecutionInputValueLimit = useWorkspaceConfigValue(
        "INPUT_VALUE_SIZE_BYTES_OPTIONAL_LOAD_LIMIT"
    );
    const optionalLoadExecutionOutputValueLimit = useWorkspaceConfigValue(
        "OUTPUT_VALUE_SIZE_BYTES_OPTIONAL_LOAD_LIMIT"
    );

    const limits = useMemo(
        () => ({
            input: {
                alwaysLoadLimit: alwaysLoadExecutionInputValueLimit,
                optionalLoadLimit: optionalLoadExecutionInputValueLimit,
            },
            output: {
                alwaysLoadLimit: alwaysLoadExecutionOutputValueLimit,
                optionalLoadLimit: optionalLoadExecutionOutputValueLimit,
            },
        }),
        [
            alwaysLoadExecutionInputValueLimit,
            alwaysLoadExecutionOutputValueLimit,
            optionalLoadExecutionInputValueLimit,
            optionalLoadExecutionOutputValueLimit,
        ]
    );

    const [showValue, setShowValue] = useState(false);

    const resourceSize = resourceData?.[`${activeTab}_value_size_bytes`];
    const isValueAlwaysShown = useMemo(() => {
        return (
            isExecution &&
            limits[activeTab].alwaysLoadLimit &&
            resourceSize <= limits[activeTab].alwaysLoadLimit
        );
    }, [activeTab, isExecution, limits, resourceSize]);

    const canShowValue = useMemo(() => {
        return isValueAlwaysShown || showValue;
    }, [isValueAlwaysShown, showValue]);

    const isValueAlwaysHidden = useMemo(() => {
        return (
            isExecution &&
            limits[activeTab].optionalLoadLimit &&
            resourceSize > limits[activeTab].optionalLoadLimit
        );
    }, [activeTab, isExecution, limits, resourceSize]);

    useEffect(() => {
        if (canShowValue && resourceSize > 0) {
            const queryString = `${
                activeTab === "input"
                    ? "\ninput_value\ninput_schema"
                    : "\noutput_value\noutput_schema"
            }`;
            setLoading(true);
            const params = {
                query: `query ($id: String!) {
                                        execution(id: $id) {
                                            ${queryString}
                                        }
                                    }`,
                variables: {
                    id: recordId,
                },
                allow_deleted: true,
            };
            graphqlPost(params)
                .then((data: any) => {
                    const response = data?.data;
                    setExecutionData(response?.execution ?? {});
                })
                .catch((error) =>
                    NotificationService.error(
                        <RestErrorNotification
                            titleOverride="Failed to fetch execution"
                            error={error}
                        />
                    )
                )
                .finally(() => setLoading(false));
        }
    }, [activeTab, canShowValue, dispatch, iri, recordId, resourceSize]);

    const value = useMemo(() => {
        return activeTabContent.valueGetter(executionData);
    }, [activeTabContent, executionData]);

    const isEmpty = useMemo(
        () =>
            isValueAlwaysShown &&
            ((activeTab === "output" && value?.values == null) ||
                (activeTab === "input" && Object.values(value?.values ?? {}).length === 0)),
        [activeTab, isValueAlwaysShown, value?.values]
    );

    return (
        <div
            key={`${activeTab}-schemaForm`}
            className="schemaFormTab"
            style={{ position: "relative" }}
        >
            <SpinnerAnimation isLoading={isLoading} />
            {isEmpty ? (
                <div>{isLoading ? "" : `No ${activeTab}s${isExecutionRunning ? " yet" : ""}.`}</div>
            ) : (
                <>
                    <Button
                        label={`Download ${activeTab} value`}
                        onClick={() => {
                            const typedWindow: any = window;
                            window.open(
                                `${typedWindow.REACT_APP_REST_API || ""}${
                                    typedWindow.REACT_APP_REST_API_NAMESPACE
                                }/execution/${
                                    resourceData.id
                                }?download=${activeTab}_value&allow_deleted=true`
                            );
                        }}
                        icon={IconCollection.download}
                        variation="dark"
                        disabled={isLoading}
                    />
                    <SwitchContentFormComponent
                        input={{
                            name: activeTabContent.uid,
                            value,
                            onBlur: () => {},
                            onChange: () => {},
                            onFocus: () => {},
                        }}
                        meta={{}}
                        fieldAttr={{}}
                        fieldProps={{
                            setContentShown: setShowValue,
                            isContentShown: canShowValue,
                            isContentAlwaysHidden: isValueAlwaysHidden,
                            hiddenContentTooltip:
                                "Could not display large value. To see the value, please download.",
                            content: (
                                <SchemaForm
                                    inputProps={{
                                        value,
                                        isReadOnly: true,
                                    }}
                                />
                            ),
                        }}
                    />
                </>
            )}
        </div>
    );
};
